import React, { useEffect, useMemo, useState } from 'react';
import { Line } from '@ant-design/plots'
import dayjs from 'dayjs'
import BigNumber from 'bignumber.js';
import Container from '../Container';
import { readContractOnce, multiReadContract } from '../../contracts/utils/readContract';
import { soulContract, soulDetailContract, kogeContract, govBNBContract, nftContract } from '../../contracts/contracts';
import { useAccount, useBalance, useWaitForTransactionReceipt } from 'wagmi';
import { useMintSoulSpToken } from '../hooks/useSoul';
import { getSoulRanking } from '../../utils/axios';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Popover } from 'antd';

const getTasks = (t: any) => [
  {
    icon: '/soul/soul-own.svg',
    title: t('soul_task_own_nft_title'),
    reward: t('soul_task_own_nft_reward'),
    rewardIcon: '/soul/icon-soul.svg',
    rewardUnit: t('soul_task_own_nft_reward_unit'),
    action: t('soul_task_own_nft_action'),
    actionLink: 'https://www.48.club/nft',
    pointType: 'nftPoint',
  },
  {
    icon: '/soul/soul-hold.svg',
    title: t('soul_task_hold_koge_title'),
    reward: t('soul_task_hold_koge_reward'),
    rewardIcon: '/soul/icon-soul.svg',
    rewardUnit: t('soul_task_hold_koge_reward_unit'),
    action: t('soul_task_hold_koge_action'),
    actionLink: 'https://pancakeswap.finance/swap?outputCurrency=0xe6DF05CE8C8301223373CF5B969AFCb1498c5528',
    pointType: 'kogePoint',
  },
  {
    icon: '/soul/soul-stake.svg',
    title: t('soul_task_stake_koge_title'),
    reward: t('soul_task_stake_koge_reward'),
    rewardIcon: '/soul/icon-soul.svg',
    rewardUnit: t('soul_task_stake_koge_reward_unit'),
    action: t('soul_task_stake_koge_action'),
    actionLink: 'https://www.48.club/staking',
    pointType: 'stakePoint',
    available: {
      type: 'Koge',
    }
  },
  {
    icon: '/soul/soul-stake.svg',
    title: t('soul_task_stake_bnb_title'),
    reward: t('soul_task_stake_bnb_reward'),
    rewardIcon: '/soul/icon-soul.svg',
    rewardUnit: t('soul_task_stake_bnb_reward_unit'),
    action: t('soul_task_stake_bnb_action'),
    actionLink: 'https://www.48.club/delegate',
    pointType: 'bscStakePoint',
    available: {
      type: 'BNB',
    }
  },
  {
    icon: '/soul/soul-delegate.svg',
    title: t('soul_task_delegate_govbnb_title'),
    reward: t('soul_task_delegate_govbnb_reward'),
    rewardIcon: '/soul/icon-soul.svg',
    rewardUnit: t('soul_task_delegate_govbnb_reward_unit'),
    action: t('soul_task_delegate_govbnb_action'),
    actionLink: 'https://www.48.club/delegate',
    pointType: 'govBNBPoint',
    available: {
      type: 'govBNB',
    }
  },
];
const getProgressList = (t: any) => [
  {
    title: t('soul_minted'),
    percent: 0,
    number: 0,
  },
  {
    title: t('soul_basic'),
    percent: 33,
    number: 48,
  },
  {
    title: t('soul_gold'),
    number: 100,
    percent: 66,
  },
  {
    title: t('soul_platinum'),
    number: 480,
    percent: 100,
  },
]

interface SoulPointsPanelProps {
  taskPoints: any;
  pointData: any;
  isMember: any;
  mintCallback: any;
}

const SoulPointsPanel: React.FC<SoulPointsPanelProps> = ({ taskPoints, pointData, isMember, mintCallback }) => {
  const { address } = useAccount();
  const { t } = useTranslation('soul');
  // const address = '0xa31A940ecFB4Cb9fE7884eB3C9a959Db79CbdC70'
  
  // 格式化地址显示
  const displayName = address ? `${address.slice(0, 4)}...${address.slice(-4)}` : t('soul_not_connected')
  const { handleMint, hash, isConfirmed: isMintSuccess } = useMintSoulSpToken();

  const leveMemo = useMemo(() => {  
    const data = { text: '', class: '', per: 0 }
    if (!isMember) {
      data.text = t('soul_not_minted_yet');
      data.class = 'bg-gray-400 text-[#fff]';
      return data;
    }
    console.log(pointData?.points, 'pointData?.points');
    if (pointData?.points >= 480) {
      data.text = t('soul_platinum_member');
      data.class = 'bg-[#2ED3B7] text-[#000]';
      data.per = 100;
    } else if (pointData?.points >= 100) {
      data.text = t('soul_gold_member');
      data.class = 'bg-[#F670C7] text-[#000]';
      data.per = 68
    } else if (pointData?.points >= 48) {
      data.text = t('soul_basic_member');
      data.class = 'bg-[#FFD166] text-[#000]';
      data.per = 35
    } else {
      data.text = t('soul_not_enough_points');
      data.class = 'bg-gray-400 text-[#fff]';
      data.per = 4
    }
    return data;
  }, [pointData?.points, isMember, t]);
  const reverseData = (pointData?.detail || []).slice().reverse()
  
  // 准备多系列数据
  const chartData = useMemo(() => {
    if (!reverseData || reverseData.length === 0) return [];
    
    // 历史数据线
    const historyLine = reverseData.map((item: any) => ({
      snap_time: item.snap_time,
      points: item.points,
      type: 'history'
    }));
    
    // 当前积分固定线
    const currentLine = reverseData.map((item: any) => ({
      snap_time: item.snap_time,
      points: pointData?.points || 0,
      type: 'average'
    }));
    
    return [...historyLine, ...currentLine];
  }, [reverseData, pointData?.points]);
  console.log(chartData, 'chartData');
  // 图表配置
  const chartConfig = {
    autoResize: true,
    data: chartData,
    "theme": "classicDark",
    xField: 'snap_time',
    yField: 'points',
    colorField: 'type',
    smooth: true,
    // title: t('soul_chart_title'),

    scale: { color: { range: ['#30BF78', '#F4664A'] } },
    lineStyle: {
      lineWidth: 2,
    },
    // point: {
    //   size: 2,
    //   shape: 'circle',
    // },
    axis: {
      x: {
        grid: false,
        gridLineWidth: 2,
        gridStroke: '#fff',
        gridLineDasharray: [4, 4],
        gridLineOpacity: 1,
        gridLineStyle: 'dashed',
        gridLineAlign: 'center',
        gridLineOffset: 0,
        line: true, // 是否显示轴线
        lineLineWidth: 1, // 轴线宽度
        lineStroke: '#fff', // 轴线描边色
        label: true, // 是否显示刻度值
        labelFontSize: 12, // 刻度值文字大小
        labelFill: '#fff', // 刻度值字体颜色
        labelFontWeight: 500, // 刻度值字体粗细
        labelFormatter: (d: any) => {
          return dayjs(d).format('MM-DD')
        },
        gridFilter: (datum: any, index: any, data: any) => {
          if (pointData?.detail.length < 10) {
            return true
          } else if (pointData?.detail.length < 20) {
            return index % 2 === 0;
          } else if (pointData?.detail.length < 30) {
            return index % 3 === 0;
          } else if (pointData?.detail.length < 40) {
            return index % 5 === 0;
          }
          return true;
        },
        labelFilter: (datum: any, index: any, data: any) => {
          if (pointData?.detail.length < 10) {
            return true
          } else if (pointData?.detail.length < 20) {
            return index % 2 === 0;
          } else if (pointData?.detail.length < 30) {
            return index % 3 === 0;
          } else if (pointData?.detail.length < 49) {
            return index % 6 === 0;
          }
          return true;
        },
      },
      y: {
        grid: true,
        gridLineWidth: 2,
        gridStroke: '#fff',
        gridLineDasharray: [4, 4],
        gridLineOpacity: 1,
        gridLineStyle: 'dashed',
        gridLineAlign: 'center',
        gridLineOffset: 0,
        line: true, // 是否显示轴线
        lineLineWidth: 1, // 轴线宽度
        lineStroke: '#fff', // 轴线描边色
        label: true, // 是否显示刻度值
        labelFontSize: 12, // 刻度值文字大小
        labelFill: '#fff', // 刻度值字体颜色
        labelFontWeight: 500, // 刻度值字体粗细
      },
    },
    legend: {
      position: 'top',
    },
    interaction: {
      legengFilter: false, // 启用图例筛选交互
    }
  }
  const handleMintClick = async () => {
    try {
      await handleMint()
      mintCallback();
    } catch (error) {
      console.error('Error minting soul sp token:', error);
    }
  }
  useEffect(() => {
    if (isMintSuccess) {
      mintCallback();
    }
  }, [isMintSuccess]);

  const tasks = isMember ? getTasks(t) : getTasks(t).filter((task: any) => task.pointType === 'kogePoint');
  // const tasks = getTasks(t);
  const progressList = getProgressList(t);
  console.log(taskPoints, 'taskPoints');
  
  return (
    <div className='px-[12px]'>
      <Container className='relative border border-solid bg-gray-950 border-gray-700 rounded-[16px] p-[28px] shadow-[0px_4px_24px_12px_rgba(224,144,90,0.08)] !w-auto'>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-8 mb-8">
          {/* Left: Points Collected */}
          <div className="flex-1 justify-center flex flex-col py-[44px] px-[20px] sm:px-[0px] sm:w-[50%]">
            <div className="flex items-center gap-2 text-lg font-bold justify-center text-center ">
              <img src="/soul/icon-soul.svg" alt="point" className="w-5 h-5" />
              {t('soul_points_collected')}
            </div>
            <div className=" text-16 mb-1 text-center text-gray-500">{displayName}</div>
            <div className="text-[30px] sm:text-[60px] font-extrabold text-white text-center mt-[38px] mb-[12px]">{pointData?.points || 0}</div>
            <div className={`text-16 sm:text-24 p-[8px] font-semibold block text-center w-fit mx-auto mb-[52px] ${leveMemo.class}`}>
              {leveMemo.text}</div>
            {/* Progress Bar */}
            <div className="w-full mx-auto px-[22px]">
              <div className="relative w-full bg-gray-800 h-[1px]">
                <div className="absolute h-[1px] bg-[#FEC84B]" style={{ width: `${leveMemo.per}%` }}></div>
              </div>
              <div className="relative -top-[14px]">
                {
                  progressList.map((item, idx) => (
                    <div key={idx} className={`flex flex-col items-center absolute`} style={{ left: `${item.percent}%`, transform: 'translateX(-48%)' }}>
                      {
                        (pointData?.points || 0) >= item.number && isMember ? (<div className='w-[28px] h-[28px] rounded-full bg-[#FEC84B] flex items-center justify-center'>
                          <CheckOutlined className='text-white' />
                        </div>)
                        : (<div className='w-[28px] h-[28px] rounded-full bg-gray-800 flex items-center justify-center'>
                          <div className="w-[14px] h-[14px] rounded-full bg-gray-700"></div>
                        </div>)
                      }
                      
                      <div className="text-xs mt-[10px] text-[#fff]">{item.number}</div>
                      <div className="text-xs text-gray-400">{item.title}</div>
                    </div>
                  ))
                }
              </div>
              
            </div>
          </div>
          {/* Right: Illustration & Mint */}
          {
            !isMember && (
              <div className="flex-1 flex flex-col items-center justify-center py-[20px] px-[0px] sm:w-[50%]">
                <img src="/soul/soul-box.svg" alt="box" className="w-24 mb-4" />
                <div className="text-gray-500 text-16 my-[32px] text-center">
                  {t('soul_mint_description')}
                </div>
                <Popover content={+taskPoints.koge < 1 ? t('soul_mint_point_hint') : '' } title={''}>
                  <button onClick={handleMintClick} disabled={ +taskPoints.koge < 1} className="bg-white text-black font-bold px-[20px] py-[12px] rounded-[1000px] shadow hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed">
                    {t('soul_mint_button')}
                  </button>
                </Popover>
                
              </div>
            )
          }
          {
            isMember && (
              <div className="flex-1 flex flex-col items-center justify-center py-[20px] px-[0px] sm:w-[50%]">
                {
                  chartData.length > 0 ? (
                    <Line className='w-full' {...chartConfig} />
                  ): (
                    <div className="text-gray-500 text-16 font-normal text-left">
                      ---
                    </div>
                  )
                }
              </div>
            )
          }
          
        </div>
        {/* Task List */}
        <div className="flex-col gap-[12px] hidden sm:flex">
          {tasks.map((task, idx) => (
            <div key={idx} className={`flex items-center justify-between rounded-xl bg-gray-800 p-[28px] shadow`}>
              <div className="flex items-center gap-4 w-[25%]">
                <img src={task.icon} alt={task.title} className="w-10 h-10" />
                <div className="text-white font-bold">{task.title}</div>
              </div>
              <div className="flex items-center gap-1 text-[#FFD166] font-bold w-[25%]">
                <div className="text-white text-24 font-extrabold">
                  {task.reward}
                </div>
                <img src={task.rewardIcon} alt="point" className="w-6 h-6" />
                <span className="text-16 font-normal leading-[24px] text-white">{task.rewardUnit}</span>
              </div>
              <div className="flex items-center gap-8 w-[25%]">
                {
                  isMember && (<div className="text-gray-500 text-16 font-normal text-left">
                    {t('soul_earned_points')}<br />
                    <span className="text-white text-16 font-normal">{taskPoints[task.pointType] || 0}</span>
                  </div>)
                }
                
              </div>
              <div className="w-[25%] flex justify-end">
                {
                  task.available && address && (
                    <div className="text-gray-500 text-16 font-normal mr-[20px] text-right">
                      <div>{t('soul_available')} {task.available.type}</div>
                      <div className='text-white'>{taskPoints[task.available.type.toLowerCase()] || 0}</div>
                    </div>
                  )
                }

                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href={task.actionLink}
                  className={`w-[90px] py-2 text-[14px] rounded-[100px] font-bold bg-white text-black hover:bg-gray-100 transition text-center h-fit`}
                >
                  {task.action}
                </a>
              </div>
              
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-[12px] sm:hidden">
          {tasks.map((task, idx) => (
            <div key={idx} className={`items-center rounded-xl bg-gray-800 p-[16px] shadow`}>
              <div className="relative text-18 mb-[20px]">
                <img src={task.icon} alt={task.title} className="w-[64px] h-[64px] absolute top-0 right-0" />
                <div className="text-white font-bold">{task.title}</div>
                <div className="flex items-center gap-1 text-[#FFD166] font-bold mt-[4px]">
                  <div className="text-white font-extrabold">
                    {task.reward}
                  </div>
                  <img src={task.rewardIcon} alt="point" className="w-6 h-6" />
                  <span className="text-16 font-normal leading-[24px] text-white">{task.rewardUnit}</span>
                </div>
              </div>
              <div className="flex items-center gap-8 w-full mb-[20px]">
                {isMember && (
                <div className="text-gray-500 text-16 font-normal text-left w-[50%]">
                    {t('soul_earned_points')}<br />
                    <span className="text-white text-16 font-normal">{taskPoints[task.pointType] || 0}</span>
                  </div>
                )}
                {
                  task.available && address && (
                    <div className="text-gray-500 text-16 font-normal mr-[20px] text-left w-[50%]">
                      <div>{t('soul_available')} {task.available.type}</div>
                      <div className='text-white'>{taskPoints[task.available.type.toLowerCase()] || 0}</div>
                    </div>
                  )
                }
              </div>
              <div className="w-full flex justify-end">

                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href={task.actionLink}
                  className={`w-full py-2 text-[14px] rounded-[100px] font-bold bg-white text-black hover:bg-gray-100 transition text-center h-fit`}
                >
                  {task.action}
                </a>
              </div>
              
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default SoulPointsPanel;